import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { IconButton, Column, Row, Input, Label } from 'components/Blocks';
import { XIcon } from 'components/Icons';

import { useToggleV2 } from 'hooks';
import { REGEX } from 'utils/constants';

interface MultipleEmailsInputProps {
  children?: never;
  emails: string[];
  loading?: boolean;
  updateEmails: (emails: string[]) => void;
}

export function MultipleEmailsInput({
  emails,
  updateEmails,
  loading,
}: MultipleEmailsInputProps): JSX.Element {
  const [email, setEmail] = useState('');

  const [error, errorHandlers] = useToggleV2();

  const { t } = useLang();

  const addEmail = ({ key }: { key: string }): void => {
    if (key !== 'Enter') return;

    const toAdd = email.toLocaleLowerCase().trim();

    const isEmail = REGEX.EMAIL.test(toAdd);

    if (!isEmail) return errorHandlers.turnOn();

    if (emails.includes(toAdd)) return setEmail('');

    errorHandlers.turnOff();

    updateEmails([...emails, toAdd]);

    setEmail('');
  };

  const removeEmail = (toRemove: string): void => {
    updateEmails(emails.filter((e) => e !== toRemove));
  };

  return (
    <Column gap="3">
      <Row gap="2" justifyContent="space-between">
        <Label>Emails</Label>

        <p className="text-xxs italic opacity-90">{t('press-enter-to-add')}</p>
      </Row>

      {emails.map((clientEmail) => (
        <Row gap="2" key={clientEmail}>
          <Input className="shrink-1 w-full" value={clientEmail} disabled />

          <IconButton onClick={() => removeEmail(clientEmail)}>
            <XIcon />
          </IconButton>
        </Row>
      ))}

      <Input
        value={email}
        onChange={({ target }) => setEmail(target.value)}
        disabled={loading}
        onKeyDown={addEmail}
        className={error ? 'border-destructive' : ''}
      />

      {error && (
        <p className="text-xs italic text-destructive">
          {t('please-make-sure-its-actually-an-email')}
        </p>
      )}
    </Column>
  );
}
